'use client';

import { type MouseEvent, memo, type ReactNode, useCallback, useMemo, useRef, useState } from 'react';
import { useResizeDetector } from 'react-resize-detector';

import { type Clip, ClipType } from '@air/api/types';
import {
  TRUNCATED_TEXT_TOOLTIP_CONTENT_CLASS,
  TruncatedTextWithTooltip,
} from '@air/component-truncated-text-with-tooltip';
import { FileVideoCameraIcon, ImageIcon, LayersIcon } from '@air/design-icons';
import { Tooltip } from '@air/primitive-tooltip';
import { VisuallyHidden } from '@air/primitive-visually-hidden';
import { formatBytes } from '@air/utilities';
import { cn } from '@air/utils-tailwind';

import { GalleryAssetCardBadge } from '~/components/gallery/GalleryAssetCardBadge';
import { VideoDurationBadge } from '~/components/gallery/VideoDurationBadge';
import { VideoScrubbableThumbnail } from '~/components/VideoScrubbableThumbnail';
import { useVideoScrubbableThumbnail } from '~/hooks/useVideoScrubbableThumbnail';
import { imgixLoader } from '~/utils/imgixLoader';

export type ImageRenderProps = {
  alt: string;
  className: string;
  onError: () => void;
  src: string;
};

export type GalleryAssetCardProps = {
  clip: Clip;
  isDisabled?: boolean;
  isSelected?: boolean;
  onClick?: (clip: Clip, event: MouseEvent<HTMLButtonElement>) => void;
  onContextMenu?: (clip: Clip, event: MouseEvent<HTMLButtonElement>) => void;
  onDoubleClick?: (clip: Clip, event: MouseEvent<HTMLButtonElement>) => void;
  onTitleClick?: (clip: Clip, event: MouseEvent<HTMLButtonElement>) => void;
  renderImage?: (props: ImageRenderProps) => ReactNode;
};

/** Height of metadata section below thumbnail (title + meta text + margin) */
export const ASSET_CARD_METADATA_HEIGHT = 62;

const ASSET_CARD_METADATA_TOP_MARGIN = 8;

/**
 * Gallery card for displaying an asset.
 * Fills container dimensions (provided by JustifiedGrid).
 * Thumbnail fills available space, metadata is below.
 */
export const GalleryAssetCard = memo(
  ({
    clip,
    isDisabled,
    isSelected,
    onClick,
    onContextMenu,
    onDoubleClick,
    onTitleClick,
    renderImage,
  }: GalleryAssetCardProps) => {
    const [imageError, setImageError] = useState(false);
    const [isTitleOverflowing, setIsTitleOverflowing] = useState(false);

    // Use DAR (Display Aspect Ratio) for videos when available, otherwise use standard dimensions
    const displayWidth = clip.darWidth ?? clip.width;
    const displayHeight = clip.darHeight ?? clip.height;

    // Use image for display, fallback to original for non-image files
    // biome-ignore lint/suspicious/noUnnecessaryConditions: Original-only assets can omit image at runtime despite the API type.
    const thumbnailUrl = clip.assets.image ?? clip.assets.original;

    const title = clip.title ?? clip.importedName ?? 'Untitled';

    const optimizedThumbnailUrl = useMemo(() => {
      if (!thumbnailUrl) {
        return undefined;
      }

      return imgixLoader({ src: thumbnailUrl, width: 400 });
    }, [thumbnailUrl]);

    const meta = useMemo(() => {
      const parts: string[] = [];

      if (clip.ext) {
        parts.push(clip.ext.toUpperCase());
      }

      if (clip.size) {
        parts.push(formatBytes(clip.size));
      }

      if (displayWidth && displayHeight) {
        parts.push(`${displayWidth}×${displayHeight}`);
      }

      return parts.join(' · ');
    }, [clip.ext, clip.size, displayWidth, displayHeight]);

    const handleClick = useCallback(
      (event: MouseEvent<HTMLButtonElement>) => {
        onClick?.(clip, event);
      },
      [clip, onClick],
    );

    const handleContextMenu = useCallback(
      (event: MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
        onContextMenu?.(clip, event);
      },
      [clip, onContextMenu],
    );

    const handleDoubleClick = useCallback(
      (event: MouseEvent<HTMLButtonElement>) => {
        onDoubleClick?.(clip, event);
      },
      [clip, onDoubleClick],
    );

    const handleTitleClick = useCallback(
      (event: MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        onTitleClick?.(clip, event);
      },
      [clip, onTitleClick],
    );

    const handleImageError = useCallback(() => {
      setImageError(true);
    }, []);

    const isVideo = clip.type === ClipType.video;
    const hasMultipleVersions = clip.version > 1;
    const showPlaceholder = !optimizedThumbnailUrl || imageError;

    const [isHovered, setIsHovered] = useState(false);
    const thumbnailRef = useRef<HTMLDivElement>(null);
    const { width: thumbnailWidth = 0, height: thumbnailHeight = 0 } = useResizeDetector<HTMLDivElement>({
      targetRef: thumbnailRef,
    });

    const spriteSheet = clip.assets.spriteSheet;
    const isScrubbable = isVideo && !!spriteSheet?.src && thumbnailWidth > 0 && thumbnailHeight > 0;
    const showScrubPreview = isScrubbable && isHovered;

    const { backgroundImageRef, backgroundImageStyles, indicatorRef, onMouseLeave, onMouseMove } =
      useVideoScrubbableThumbnail({
        containerDimensions: { height: thumbnailHeight, width: thumbnailWidth },
        scrubRegionRef: thumbnailRef,
        spritesheet: spriteSheet,
      });

    const handleMouseEnter = useCallback(() => {
      setIsHovered(true);
    }, []);

    const handleMouseLeave = useCallback(() => {
      setIsHovered(false);
      onMouseLeave();
    }, [onMouseLeave]);

    const imageNode = useMemo(() => {
      const imageProps: ImageRenderProps = {
        alt: title,
        className: 'absolute inset-0 size-full object-cover',
        onError: handleImageError,
        src: optimizedThumbnailUrl ?? '',
      };

      return renderImage ? (
        renderImage(imageProps)
      ) : (
        // biome-ignore lint/performance/noImgElement: External CDN image, not using Next.js Image
        // biome-ignore lint/a11y/useAltText: alt is provided via imageProps spread
        <img {...imageProps} loading="lazy" />
      );
    }, [renderImage, handleImageError, optimizedThumbnailUrl, title]);

    return (
      // biome-ignore lint/a11y/noStaticElementInteractions: mouse handlers drive the hover scrub preview only; the card's interactive element is the inner overlay button
      <div
        className={cn(
          'group/asset relative flex size-full flex-col rounded-16 border-2 border-transparent p-0.5 transition-colors hover:bg-neutral-4',
          isDisabled && 'cursor-not-allowed opacity-50',
          isSelected && 'border-brand-9',
        )}
        data-asset-id={clip.id}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onMouseMove={isScrubbable ? onMouseMove : undefined}
      >
        <div className="relative flex-1 overflow-hidden rounded-12 bg-neutral-4" ref={thumbnailRef}>
          {showPlaceholder ? (
            <div className="absolute inset-0 flex items-center justify-center text-neutral-8">
              <ImageIcon size="md" />
            </div>
          ) : (
            imageNode
          )}

          {showScrubPreview && spriteSheet?.src && (
            <VideoScrubbableThumbnail
              backgroundImageRef={backgroundImageRef}
              indicatorRef={indicatorRef}
              src={spriteSheet.src}
              style={backgroundImageStyles}
            />
          )}

          {!showScrubPreview && <VideoDurationBadge className="absolute right-2 bottom-2" clip={clip} />}

          {(isVideo || hasMultipleVersions) && (
            <div className="absolute top-2 left-2 flex gap-1.5">
              {isVideo && (
                <GalleryAssetCardBadge>
                  <FileVideoCameraIcon size="xs" />
                </GalleryAssetCardBadge>
              )}
              {hasMultipleVersions && (
                <GalleryAssetCardBadge>
                  <LayersIcon size="xs" />
                </GalleryAssetCardBadge>
              )}
            </div>
          )}
        </div>

        <div
          className="relative mt-2 min-w-0 px-2 pb-3"
          style={{ height: ASSET_CARD_METADATA_HEIGHT - ASSET_CARD_METADATA_TOP_MARGIN }}
        >
          {onTitleClick ? (
            <button className="relative z-10 w-full min-w-0 text-left" onClick={handleTitleClick} type="button">
              <TruncatedTextWithTooltip
                className="block font-medium text-16 text-neutral-12 leading-normal hover:underline"
                text={title}
              />
            </button>
          ) : (
            <TruncatedTextWithTooltip
              className="block font-medium text-16 text-neutral-12 leading-normal"
              onOverflowChange={setIsTitleOverflowing}
              text={title}
            />
          )}
          {meta && (
            <p className="truncate font-medium text-12 text-neutral-9 uppercase leading-[133%] tracking-wider">
              {meta}
            </p>
          )}
        </div>

        <Tooltip
          className={TRUNCATED_TEXT_TOOLTIP_CONTENT_CLASS}
          label={!onTitleClick && isTitleOverflowing ? title : undefined}
          side="top"
          sideOffset={4}
        >
          <button
            className="absolute inset-0 size-full bg-transparent [outline:none]"
            disabled={isDisabled}
            onClick={handleClick}
            onContextMenu={handleContextMenu}
            onDoubleClick={handleDoubleClick}
            title={isDisabled ? 'Selection limit reached' : undefined}
            type="button"
          >
            <VisuallyHidden>Select {title}</VisuallyHidden>
          </button>
        </Tooltip>
      </div>
    );
  },
);

GalleryAssetCard.displayName = 'GalleryAssetCard';
